// AfiliadoWizardModal — registro DIRECTO de un afiliado en una cooperativa (lo hace el
// personal). Tres pasos, con los datos que exige el flujo de Fenagro/Ecolab:
//   1. Datos personales   2. Contacto y ubicación (catálogo de geografía)   3. Cooperativa y aporte
// Al terminar, el afiliado queda «pendiente de pago»: ocupa un cupo, pero no cuenta como activo
// hasta registrar el pago de su inscripción (ahí se activa y se emite el carnet).

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  InputAdornment, InputLabel, MenuItem, Select, Step, StepLabel, Stepper, TextField, Typography,
} from '@mui/material';
import SelectorGeografia from './SelectorGeografia';
import { afiliacionApi } from '../services/coopnetApi';
import { moneda } from '../utils/formato';

const PASOS = ['Datos personales', 'Contacto y ubicación', 'Cooperativa y aporte'];
const TIPOS_DOCUMENTO = [
  ['CC', 'Cédula de ciudadanía'], ['CE', 'Cédula de extranjería'], ['TI', 'Tarjeta de identidad'], ['PA', 'Pasaporte'],
];
const GENEROS = ['Femenino', 'Masculino', 'Otro'];
const ESTADOS_CIVILES = ['Soltero(a)', 'Casado(a)', 'Unión libre', 'Divorciado(a)', 'Viudo(a)'];
const PAIS_COLOMBIA = 1045;

const INICIAL = {
  nombre: '', apellido: '', tipoDocumento: 'CC', documento: '', fechaNacimiento: '', genero: '', estadoCivil: '',
  email: '', telefono: '', paisId: PAIS_COLOMBIA, departamentoId: '', municipioId: '', direccion: '',
  cooperativaId: '', aporteMensual: '',
};
const aEntero = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));

/**
 * @param {boolean} open
 * @param {object[]} cooperativas  donde el usuario puede afiliar (activas y dentro de su alcance)
 * @param {string} [cooperativaFija]  si la vista es de una cooperativa, se fija y no se elige
 * @param {() => void} onClose
 * @param {(afiliado: object) => void} onRegistrado  recibe el afiliado creado (con su valorInscripcion)
 */
export default function AfiliadoWizardModal({ open, cooperativas, cooperativaFija = '', onClose, onRegistrado }) {
  const [paso, setPaso] = useState(0);
  const [f, setF] = useState(INICIAL);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setPaso(0);
    setError('');
    setF({ ...INICIAL, cooperativaId: cooperativaFija || (cooperativas.length === 1 ? cooperativas[0].id : '') });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (parcial) => setF((x) => ({ ...x, ...parcial }));
  const coop = useMemo(() => cooperativas.find((c) => c.id === f.cooperativaId), [cooperativas, f.cooperativaId]);

  const puedeAvanzar =
    paso === 0 ? Boolean(f.nombre.trim() && f.apellido.trim() && f.documento.trim())
    : paso === 1 ? !f.email.trim() || f.email.includes('@')
    : Boolean(f.cooperativaId);

  const guardar = async () => {
    setEnviando(true);
    setError('');
    try {
      const afiliado = await afiliacionApi.registrar(f.cooperativaId, {
        nombre: f.nombre, apellido: f.apellido, tipoDocumento: f.tipoDocumento, documento: f.documento,
        fechaNacimiento: f.fechaNacimiento || null, genero: f.genero || null, estadoCivil: f.estadoCivil || null,
        email: f.email, telefono: f.telefono, paisId: aEntero(f.paisId), departamentoId: aEntero(f.departamentoId),
        municipioId: aEntero(f.municipioId), direccion: f.direccion, aporteMensual: Number(f.aporteMensual) || 0,
      });
      onRegistrado(afiliado);
      onClose();
    } catch (e) {
      setError(e?.message || 'No se pudo registrar el afiliado.');
    } finally {
      setEnviando(false);
    }
  };

  const grid = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 };
  const texto = (label, campo, extra = {}) => (
    <TextField label={label} value={f[campo]} onChange={(e) => set({ [campo]: e.target.value })} fullWidth {...extra} />
  );
  const lista = (label, campo, opciones) => (
    <FormControl fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={f[campo]} onChange={(e) => set({ [campo]: e.target.value })}>
        <MenuItem value="">—</MenuItem>
        {opciones.map((o) => (Array.isArray(o) ? <MenuItem key={o[0]} value={o[0]}>{o[1]}</MenuItem> : <MenuItem key={o} value={o}>{o}</MenuItem>))}
      </Select>
    </FormControl>
  );

  return (
    <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Nuevo afiliado
        <Typography variant="body2" color="text.secondary">Registro directo en la cooperativa</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={paso} alternativeLabel sx={{ mb: 3 }}>
          {PASOS.map((p) => (<Step key={p}><StepLabel>{p}</StepLabel></Step>))}
        </Stepper>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {paso === 0 && (
          <Box sx={grid}>
            {texto('Nombres *', 'nombre', { autoFocus: true })}
            {texto('Apellidos *', 'apellido')}
            {lista('Tipo de documento', 'tipoDocumento', TIPOS_DOCUMENTO)}
            {texto('Número de documento *', 'documento')}
            {texto('Fecha de nacimiento', 'fechaNacimiento', { type: 'date', InputLabelProps: { shrink: true } })}
            {lista('Género', 'genero', GENEROS)}
            {lista('Estado civil', 'estadoCivil', ESTADOS_CIVILES)}
          </Box>
        )}

        {paso === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={grid}>
              {texto('Correo electrónico', 'email', { type: 'email', error: Boolean(f.email) && !f.email.includes('@'), helperText: 'Opcional' })}
              {texto('Teléfono', 'telefono')}
            </Box>
            <SelectorGeografia
              nivel="municipio"
              valor={{ paisId: f.paisId, departamentoId: f.departamentoId, municipioId: f.municipioId }}
              onChange={(g) => set({ paisId: g.paisId, departamentoId: g.departamentoId, municipioId: g.municipioId })}
            />
            {texto('Dirección', 'direccion')}
          </Box>
        )}

        {paso === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Cooperativa *</InputLabel>
              <Select label="Cooperativa *" value={f.cooperativaId} onChange={(e) => set({ cooperativaId: e.target.value })} disabled={Boolean(cooperativaFija)}>
                {cooperativas.map((c) => (<MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>))}
              </Select>
            </FormControl>
            {texto('Aporte mensual (cuota de sostenimiento)', 'aporteMensual', {
              type: 'number', InputProps: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
            })}
            {coop && (
              <Alert severity="info">
                Valor de la inscripción: <b>{moneda(coop.valorInscripcion)}</b>. El afiliado quedará <b>pendiente de pago</b> y
                ocupará un cupo; se activa y recibe su carnet al registrar ese pago.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
        <Box sx={{ flex: 1 }} />
        {paso > 0 && <Button onClick={() => setPaso((p) => p - 1)} disabled={enviando}>Atrás</Button>}
        {paso < PASOS.length - 1 ? (
          <Button variant="contained" onClick={() => setPaso((p) => p + 1)} disabled={!puedeAvanzar}>Siguiente</Button>
        ) : (
          <Button variant="contained" onClick={guardar} disabled={!puedeAvanzar || enviando} startIcon={enviando ? <CircularProgress size={18} color="inherit" /> : null}>
            Registrar afiliado
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
